import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { interplayShellEnabled } from './config';
import { themeAccent, type EtBarModel } from './etBarModel';
import { termsFor, type SectorTerms } from './sectorTerms';
import { useInterplayShellStore } from './shellStore';

/** Loads the shell for the active company (flag on only). Mounted once, in AppShell. */
export function useInterplayShellLoader(): void {
  const enabled = interplayShellEnabled();
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const companyName = useAuthStore((s) => {
    const c = s.companies.find((x) => x.company_id === s.activeCompanyId);
    return c?.group_name || c?.company_name || null;
  });
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const companyColour = useAuthStore(
    (s) => s.companies.find((x) => x.company_id === s.activeCompanyId)?.company_colour ?? null,
  );
  const load = useInterplayShellStore((s) => s.load);
  const reset = useInterplayShellStore((s) => s.reset);
  useEffect(() => {
    if (!enabled) return;
    if (!isAuthenticated || !activeCompanyId) {
      reset();
      return;
    }
    void load(activeCompanyId, companyName, companyColour);
  }, [enabled, isAuthenticated, activeCompanyId, companyName, companyColour, load, reset]);
}

/** The ET bar model, or null (flag off, not connected, or not loaded yet). */
export function useEtBarModel(): EtBarModel | null {
  const model = useInterplayShellStore((s) => s.model);
  return interplayShellEnabled() ? model : null;
}

/** The customer's accent for the app theme, contrast-safe; undefined keeps the company colour. */
export function useInterplayAccent(): string | undefined {
  const model = useEtBarModel();
  const darkMode = useUIStore((s) => s.darkMode);
  return themeAccent(model, darkMode);
}

/** The customer's words for its sector; AnooreHR's own words when off or unknown. */
export function useSectorTerms(): SectorTerms {
  const model = useEtBarModel();
  return termsFor(model?.sector ?? null);
}
